/**
 * Every scans query lives under one key, so one call refreshes them all (DEC-191
 * decision 5).
 *
 * Before DEC-191 the sidebar polled every 30 s and was never invalidated, and the Past
 * evaluations list never polled at all, so a finished scan read "running" while the page
 * stayed open. Now: lists poll while anything is live, and every list is invalidated the
 * moment a scan's stream ends.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query'
import { scansApi } from '../api/scans'
import type { LiveScansResponse } from '../types/api'

/** The prefix of every scans list query: dashboard pages, repository history, live,
 *  pending. The single-scan query (`['scan', id]`) is separate and refreshed with it. */
export const SCANS_KEY = ['scans'] as const
export const LIVE_SCANS_KEY = ['scans', 'live'] as const

/** How often the live summary is refetched while anything runs, waits or is paused. */
export const LIVE_POLL_MS = 4000

/** Refresh every scans list and every single-scan query. Call when a scan's stream ends,
 *  and after anything that changes a scan (start, stop, pause, resume, delete). */
export function invalidateScanQueries(queryClient: QueryClient): Promise<void> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: SCANS_KEY }),
    queryClient.invalidateQueries({ queryKey: ['scan'] }),
  ]).then(() => undefined)
}

/** True when the summary holds anything that is still moving or waiting. */
export function anythingLive(data: LiveScansResponse | undefined): boolean {
  if (!data) return false
  return data.scans.length > 0 || data.pending > 0
}

/** The live summary, shared by the sidebar badge and the dashboard (one request, one
 *  cache entry). Polls only while something is live, and stops when nothing is. */
export function useLiveScans() {
  return useQuery({
    queryKey: LIVE_SCANS_KEY,
    queryFn: () => scansApi.live(),
    refetchInterval: (query) => (anythingLive(query.state.data) ? LIVE_POLL_MS : false),
    retry: false,
  })
}
